import { useId } from "react";
import { Check } from "lucide-react";
import type { Language } from "../contexts/LanguageContext";
import { useLanguage } from "../contexts/LanguageContext";
import { cn } from "../lib/utils";
import { Combobox } from "./ui/combobox";

interface Props {
  labels: {
    label: string;
    pt: string;
    en: string;
    searchPlaceholder: string;
    emptyMessage: string;
  };
  className?: string;
  variant?: "dropdown" | "cards";
}

export function LanguageSelect({ labels, className = "", variant = "dropdown" }: Props) {
  const { lang, setLang } = useLanguage();
  const radioGroupName = useId();
  const options: Array<{ value: Language; label: string }> = [
    { value: "pt", label: labels.pt },
    { value: "en", label: labels.en },
  ];

  if (variant === "cards") {
    return (
      <fieldset className={cn("space-y-2", className)}>
        <legend className="text-sm font-medium text-neutral-800">
          {labels.label}
        </legend>
        <div className="grid gap-2">
          {options.map(({ value, label }) => {
            const selected = lang === value;
            return (
              <label
                key={value}
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 bg-white p-4 transition hover:border-neutral-300",
                  selected && "border-[#71B27B] bg-[#71B27B]/10"
                )}
              >
                <input
                  type="radio"
                  name={radioGroupName}
                  value={value}
                  checked={selected}
                  onChange={() => setLang(value)}
                  className="sr-only"
                  aria-label={label}
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300",
                    selected && "border-[#71B27B] bg-[#71B27B]"
                  )}
                >
                  {selected && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="text-sm font-medium text-neutral-900">
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return (
    <div className={cn("w-28", className)}>
      <Combobox
        options={options.map(({ value, label }) => ({ value, label }))}
        value={lang}
        onChange={(value) => setLang(value as Language)}
        placeholder={labels.label}
        searchPlaceholder={labels.searchPlaceholder}
        emptyMessage={labels.emptyMessage}
        ariaLabel={labels.label}
        withSearch={false}
      />
    </div>
  );
}

export default LanguageSelect;
